import { MarginBonusTypes } from '../Assignments/enum';
import {
  IAssignmentForm,
  IAssignmentSplitFormDetails,
  ISplitForm,
  ITimeOffEntryForm,
} from '../Assignments/models/IAssignmentSplitForm';
import { IPeopleSoftMarginDetailsRequest } from './IPeopleSoftCalculation';
import { IUser } from 'oidc/user.redux';
import { transformReimbursementForPeopleSoftCalculation } from '../Assignments/Reimbursement/helper';
import { mapAdditionalCostsForPeopleSoftCalculation } from '../Assignments/AdditionalCosts/helper';
import { mapBonusesForPeopleSoftCalculations } from '../Assignments/Bonuses/helper';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import {
  IPlacementMarginDetailsResponseScenario,
  IPlacementMarginDetailsResponse,
  IPlacementMarginOverTimeHourlyRateItem,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.model';
import { selectPlacementMarginGroupUpdateInprogressState } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { IPlacementMarginRequestTimeOff } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.model';
import { placementMarginAssignmentActions } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.redux';

export const createRequestPayloadForPeopleSoftCalculation = (
  assignmentForm: IAssignmentForm,
  selectedScenario: IPlacementMarginDetailsResponseScenario,
  user: IUser,
  currentMarginDetail: IPlacementMarginDetailsResponse,
  timestamp: string,
  autoSave: boolean,
  existingOverTimeHourlyRateRates: IPlacementMarginOverTimeHourlyRateItem[],
): IPeopleSoftMarginDetailsRequest => {
  const requestPayload = {
    placementId: currentMarginDetail?.placementId,
    bookingPeriodId: selectedScenario?.bookingPeriodId,
    scenarioId: selectedScenario?.scenarioId,
    scenarioName: selectedScenario?.scenarioName,
    facilityId: currentMarginDetail?.facility?.facilityId,
    disciplineId: currentMarginDetail?.disciplineId,
    specialtyId: currentMarginDetail?.specialtyId,
    subspecialtyId: currentMarginDetail?.subspecialtyId,
    bookingPeriodStartDate: currentMarginDetail?.bookingPeriodStartDate,
    bookingPeriodEndDate: currentMarginDetail?.bookingPeriodEndDate,
    brandId: currentMarginDetail?.brandId,
    brand: currentMarginDetail?.brand,
    userId: user?.userInfo?.employeeId,
    autoSave,
    timestamp,
    splits: transformSplits(assignmentForm, selectedScenario, existingOverTimeHourlyRateRates),
  };
  return requestPayload;
};

/**
 *  Method to transform the assignment splits
 * @param assignmentForm
 */
const transformSplits = (
  assignmentForm: IAssignmentForm,
  selectedScenario: IPlacementMarginDetailsResponseScenario,
  existingOverTimeHourlyRateRates: IPlacementMarginOverTimeHourlyRateItem[],
) => {
  const assignmentSection = assignmentForm?.assignment;
  const assignmentSplits = assignmentForm?.assignmentSplits;
  const timeOffRequest = mapTimeRequest(assignmentForm?.timeOffRequest, selectedScenario);

  const mappedSplits = assignmentSplits?.map((currentSplit: ISplitForm) => {
    const {
      bonusesFacilitySignOnValue,
      bonusesAmnSignOnValue,
      bonusesFacilityCompletionValue,
      bonusesAmnCompletionValue,
      bonusesInconvenienceValue,
    } = extractBonusesValuesFrom(assignmentSection?.bonusesFieldArray);
    const transformCostItems = mapCostItems(currentSplit, assignmentSection);
    const mappedOverTimeHourlyRate = existingOverTimeHourlyRateRates?.find(
      rate => rate?.splitId === currentSplit?.splitId && rate?.scenarioId === selectedScenario?.scenarioId,
    );

    return {
      splitId: currentSplit?.splitId,
      splitStartDate: currentSplit?.effectiveStartDate,
      splitEndDate: currentSplit?.effectiveEndDate,
      shiftId: currentSplit?.shiftId,
      shift: currentSplit?.shift,
      hoursPerWeek: currentSplit?.hoursPerWeek,
      hoursPerShift: currentSplit?.hoursPerShift,
      billRateOvertimeFactor: parseFloat(currentSplit?.overtimeFactor) || 0,
      payRateOvertimeFactor: parseFloat(currentSplit?.payOvertimeFactor) || 0,
      billRateDoubletimeFactor: parseFloat(currentSplit?.doubleTimeFactor) || 0,
      payRateDoubletimeFactor: parseFloat(currentSplit?.payDoubletimeFactor) || 0,
      additionalPremiumPayDesc: currentSplit?.additionalPremiumPay?.label || '',
      billRateAdditionalOvertimeRate: parseFloat(currentSplit?.overtimeRate) || 0, //@To-do-margin-tool need confirmation - change name to billRateAdditionalOvertimeRate
      billRateAdditionalDoubletimeRate: parseFloat(currentSplit?.doubleTimeRate) || 0, //@To-do-margin-tool need confirmation - change name to billRateAdditionalDoubletimeRate
      billRateRegularRate: parseFloat(currentSplit?.billRate) || 0,
      billRateVms: parseFloat(currentSplit?.vmsFee) || 0,
      billRateOther: parseFloat(currentSplit?.otherFee) || 0,
      payRateRegularRate: parseFloat(currentSplit?.payRate) || 0,
      billRateOvertimeRate: 0, // need confirmation - duplicate - not in use
      billRateDoubleTimeRate: 0, // need confirmation - duplicate - not in use
      payRateDoubleTimeRate: 0, // need confirmation  does not exist on UI .
      timeOffs: timeOffRequest ?? [],
      healthInsuranceTypeId: currentSplit?.insurance?.object?.id,
      healthInsuranceType: currentSplit?.insurance?.object?.description,
      isWaitingPeriod: Boolean(currentSplit?.waitingPeriod),
      isVoidOrientationHours: Boolean(currentSplit?.voidOrientationHours),
      billRateOrientationHours: parseFloat(currentSplit?.orientationHours) || 0,
      travelArrivingTravelValue: parseFloat(assignmentSection?.travel?.arrivingTravel) || 0,
      travelEndingTravelValue: parseFloat(assignmentSection?.travel?.endingTravel) || 0,
      travelInterimTravelValue: parseFloat(assignmentSection?.travel?.interimTravel) || 0,
      bonusesFacilitySignOnValue: parseFloat(bonusesFacilitySignOnValue), // Placeholder: Adjust according to the requirement
      bonusesAmnSignOnValue: parseFloat(bonusesAmnSignOnValue), // Placeholder: Adjust according to the requirement
      bonusesFacilityCompletionValue: parseFloat(bonusesFacilityCompletionValue), // Placeholder: Adjust according to the requirement
      bonusesAmnCompletionValue: parseFloat(bonusesAmnCompletionValue), // Placeholder: Adjust according to the requirement
      bonusesInconvenienceValue: parseFloat(bonusesInconvenienceValue), // Placeholder: Adjust according to the requirement
      allowancesStipendsMealPerDiemValue: parseFloat(currentSplit?.mealPerDiem) || 0,
      allowancesStipendsLodgingPerDiemValue: parseFloat(currentSplit?.lodgingPerDiem) || 0,
      allowancesStipendsCellPhoneStipendValue: parseFloat(currentSplit?.cellPhoneStipend) || 0,
      allowancesStipendsTlaCarAllowanceValue: parseFloat(currentSplit?.tlaCarAllowance) || 0,
      allowancesStipendsTlaMealAllowanceValue: parseFloat(currentSplit?.tlaMealAllowance) || 0,
      allowancesStipendsTlaShiftCompletionBonusValue: parseFloat(currentSplit?.tlaShiftCompletionBonus) || 0,
      allowancesStipendsTlaCellPhoneStipendValue: parseFloat(currentSplit?.tlaCellPhoneStipend) || 0,
      amnHousingUtilitiesValue: parseFloat(currentSplit?.utilities) || 0,
      amnHousingStandardFurnitureValue: parseFloat(currentSplit?.standardFurniture) || 0,
      amnHousingAdditionalFurnitureValue: parseFloat(currentSplit?.additionalFurniture) || 0,
      amnHousingRentCostValue: parseFloat(currentSplit?.rentCostMonthly) || 0,
      amnHousingCoPayValue: parseFloat(currentSplit?.housingCoPay) || 0,
      amnFurnitureCoPayValue: parseFloat(currentSplit?.furnitureCoPay) || 0,
      travelAmnFlightValue: parseFloat(assignmentSection?.travel?.amnFlight) || 0,
      travelAmnRentalCartValue: parseFloat(assignmentSection?.travel?.amnRentalCar) || 0,
      amnHousingEarlyMoveInCostPerDayValue: parseFloat(currentSplit?.earlyMoveInCostPerDay) || 0,
      amnHousingEarlyMoveInDaysWaivedValue: parseFloat(currentSplit?.earlyMoveInDaysWaived) || 0,
      amnHousingLateMoveOutCostPerDayValue: parseFloat(currentSplit?.lateMoveOutCostPerDay) || 0,
      amnHousingLateMoveOutDaysWaivedValue: parseFloat(currentSplit?.lateMoveOutDaysWaived) || 0,
      payOvertimeLimitWeek: mappedOverTimeHourlyRate?.payOvertimeLimitWeek || 0,
      payOverTimeLimitBiWeekly: mappedOverTimeHourlyRate?.payOverTimeLimitBiWeekly || 0,
      payOvertimeLimitDay: mappedOverTimeHourlyRate?.payOvertimeLimitDay || 0,
      payDoubletimeLimitWeek: mappedOverTimeHourlyRate?.payDoubletimeLimitWeek || 0,
      payDoubleTimeLimitBiWeekly: mappedOverTimeHourlyRate?.payDoubleTimeLimitBiWeekly || 0,
      payDoubletimeLimitDay: mappedOverTimeHourlyRate?.payDoubletimeLimitDay || 0,
      costItems: transformCostItems,
    };
  });
  return mappedSplits;
};

/**
 *  Remove this method and update it with category type
 * @param bonusesFieldArray
 * @param bonusType
 */
const extractBonusesValuesFrom = (
  bonusesFieldArray: {
    bonusAmount: string;
    bonusType: string;
  }[],
) => {
  const bonusesFacilitySignOnValue =
    bonusesFieldArray?.find(bonus => bonus?.bonusType === MarginBonusTypes.FacilitySignOn)?.bonusAmount ?? '0';
  const bonusesAmnSignOnValue =
    bonusesFieldArray?.find(bonus => bonus?.bonusType === MarginBonusTypes.AMNSignOn)?.bonusAmount ?? '0';
  const bonusesFacilityCompletionValue =
    bonusesFieldArray?.find(bonus => bonus?.bonusType === MarginBonusTypes.FacilityCompletion)?.bonusAmount ?? '0';
  const bonusesAmnCompletionValue =
    bonusesFieldArray?.find(bonus => bonus?.bonusType === MarginBonusTypes.AMNCompletion)?.bonusAmount ?? '0';
  const bonusesInconvenienceValue =
    bonusesFieldArray?.find(bonus => bonus?.bonusType === MarginBonusTypes.Inconvenience)?.bonusAmount ?? '0';
  return {
    bonusesFacilitySignOnValue,
    bonusesAmnSignOnValue,
    bonusesFacilityCompletionValue,
    bonusesAmnCompletionValue,
    bonusesInconvenienceValue,
  };
};

/**
 *
 * @param timeOffRequest - time off request in assignment container
 * @param selectedScenario - selected scenario
 */
const mapTimeRequest = (
  timeOffRequest: ITimeOffEntryForm[],
  selectedScenario: IPlacementMarginDetailsResponseScenario,
) => {
  return timeOffRequest?.map(timeOff => ({
    timeOffId: timeOff?.timeOffId ? Number(timeOff?.timeOffId) : 0,
    startDate: timeOff?.startDate ? timeOff.startDate : null,
    endDate: timeOff?.endDate ? timeOff.endDate : null,
    days: timeOff?.days,
    hours: timeOff?.hours,
    scenarioId: selectedScenario?.scenarioId,
    splitId: null, // need confirmation
    approvedBy: timeOff?.approvedBy,
  }));
};

/**
 * Method to map the cost items
 * Combine additional costs, reimbursements, bonuses as cost items from assignment section on UI
 */
const mapCostItems = (split: ISplitForm, assignmentSection: IAssignmentSplitFormDetails) => {
  const additionalCost = mapAdditionalCostsForPeopleSoftCalculation(split);
  const reimbursements = transformReimbursementForPeopleSoftCalculation(assignmentSection?.reimbursementFieldArray);
  const bonuses = mapBonusesForPeopleSoftCalculations(assignmentSection?.bonusesFieldArray);
  const combinedCostItems = [...additionalCost, ...reimbursements, ...bonuses];
  return combinedCostItems;
};

/**
 * Custom hook to trigger the PeopleSoft calculation
 * Extract triggerPeopleSoftCalculation calculation method from the usePeopleSoftCalculation hook
 * @param timeOffRequest - Requested time off from assignment section
 */
export const usePeopleSoftCalculation = () => {
  const dispatch = useDispatch();
  const { watch } = useFormContext();
  const isGroupUpdateInProgress = useSelector(selectPlacementMarginGroupUpdateInprogressState);

  const triggerPeopleSoftCalculation = (
    autoSave?: boolean | false,
    timeOffRequest?: ITimeOffEntryForm[] | IPlacementMarginRequestTimeOff[],
  ) => {
    if (isGroupUpdateInProgress) return;

    const formData = watch();
    // Combine formData and timeOffRequest into a single object
    const mappedFormData = {
      ...formData,
      timeOffRequest,
    };
    dispatch(
      placementMarginAssignmentActions.triggerPlacementMarginPeopleSoftCalculation({
        formData: mappedFormData,
        isDataUpdateFlow: autoSave ?? false,
        formDataRaw: formData,
      }),
    );
  };

  return { triggerPeopleSoftCalculation };
};
